
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Lead } from '@/types/lead';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';
import ErrorBoundary from '@/components/ErrorBoundary';
import CompanyLeadCard from './CompanyLeadCard';

interface CompanyLeadsTableProps {
  companyLeads: Lead[];
  isLoadingLeads: boolean;
}

const CompanyLeadsTable = ({ companyLeads, isLoadingLeads }: CompanyLeadsTableProps) => {
  const navigate = useNavigate();

  const leadsColumns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="font-medium">
          {row.original.name}
        </div>
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => {
        try {
          const emailValue = formatEmailForDisplay(row.original.email);
          const primaryEmail = getPrimaryEmail(row.original.email);
          
          if (!emailValue) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <ErrorBoundary>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a 
                  href={`mailto:${primaryEmail}`}
                  onClick={(e) => e.stopPropagation()} 
                  className="text-primary hover:underline"
                >
                  {emailValue}
                </a>
              </div>
            </ErrorBoundary>
          );
        } catch (error) {
          console.error('Error rendering email:', error);
          return <span className="text-muted-foreground">-</span>;
        }
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.status || 'New'}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/business/${row.original.id}`);
          }}
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  if (isLoadingLeads) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Associated Leads</CardTitle>
          <CardDescription>Leads connected to this company</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <p className="text-muted-foreground">Loading leads...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (companyLeads.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Associated Leads</CardTitle>
          <CardDescription>Leads connected to this company</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">No leads associated with this company yet.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/')}
            >
              Add a Lead
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Associated Leads</CardTitle>
        <CardDescription>Leads connected to this company</CardDescription>
      </CardHeader>
      <CardContent>
        <ErrorBoundary>
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <DataTable 
              columns={leadsColumns} 
              data={companyLeads}
            />
          </div>
          
          {/* Mobile/Tablet Card View */}
          <div className="lg:hidden space-y-3">
            {companyLeads.map((lead) => (
              <CompanyLeadCard 
                key={lead.id} 
                lead={lead} 
                onView={(id) => navigate(`/business/${id}`)} 
              />
            ))}
          </div>
        </ErrorBoundary>
      </CardContent>
    </Card>
  );
};

export default CompanyLeadsTable;
